import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  BookOpen,
  MessageSquare,
  Calendar,
  Radio,
  AlertTriangle,
  ArrowRight,
  UserPlus,
  UserCheck,
  CalendarPlus,
  Layers,
  GraduationCap,
  Sparkles,
} from "lucide-react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { DashboardStat } from "src/services/adminDashboardService";
import { useAdminDashboardStats } from "src/hooks/useAdminDashboardStats";
import { AdminTriageWorkstation } from "src/components/admin/dashboard/AdminTriageWorkstation";
import { supabase } from "src/lib/supabaseClient";
import { demoRequestsService } from "src/services/demoRequestsService";
import { ROUTES } from "src/routes/RouteConfig";

interface StatCardProps {
  title: string;
  stat?: DashboardStat;
  icon: React.ReactElement;
  description: string;
  isLoading: boolean;
  isError: boolean;
  linkTo: string;
}

const formatNumber = (value?: number) =>
  typeof value === "number" ? new Intl.NumberFormat("en-US").format(value) : "-";

const getMonthlyChangeBadge = (stat?: DashboardStat) => {
  if (!stat) return null;

  const difference = stat.currentMonth - stat.previousMonth;
  if (difference === 0) {
    return (
      <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
        Neutral vs last mo.
      </span>
    );
  }

  const isPositive = difference > 0;
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full border ${
        isPositive
          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
          : "text-rose-700 bg-rose-50 border-rose-200"
      }`}
    >
      {isPositive ? "+" : ""}
      {formatNumber(difference)} this mo.
    </span>
  );
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  stat,
  icon,
  description,
  isLoading,
  isError,
  linkTo,
}) => {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(linkTo)}
      className="group bg-white p-5 rounded-xl border border-slate-200/90 shadow-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
    >
      <div>
        <div className="flex items-center justify-between text-slate-500">
          <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">
            {title}
          </span>
          <span className="p-2 rounded-lg bg-slate-50 text-indigo-600 group-hover:bg-indigo-50 transition-colors">
            {icon}
          </span>
        </div>

        <div className="mt-3 flex items-baseline justify-between gap-2">
          {isLoading ? (
            <div className="h-9 w-24 animate-pulse rounded bg-slate-100" />
          ) : (
            <span className="text-3xl font-extrabold text-slate-950 font-mono tracking-tight">
              {isError ? "-" : formatNumber(stat?.total)}
            </span>
          )}
          <div>{getMonthlyChangeBadge(stat)}</div>
        </div>

        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 group-hover:text-indigo-600 font-medium transition-colors">
        <span>View detailed list</span>
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  const {
    data: dashboardStats,
    isLoading: isStatsLoading,
    isError: isStatsError,
  } = useAdminDashboardStats();

  // Fetch count of pending tutor applications awaiting verification
  const { data: pendingTutorsCount = 0 } = useQuery({
    queryKey: ["admin-pending-tutors-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("tutors")
        .select("id", { count: "exact", head: true })
        .in("verification_status", ["submitted", "under_review"]);
      return error ? 0 : count || 0;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch count of unassigned demo requests
  const { data: unassignedLeadsCount = 0 } = useQuery({
    queryKey: ["admin-unassigned-leads-count"],
    queryFn: async () => {
      const list = await demoRequestsService.fetchRecentNew(30);
      return list.filter((r) => !r.status || r.status === "new" || r.status === "unassigned").length;
    },
    staleTime: 3 * 60 * 1000,
  });

  useEffect(() => {
    setTitle("Operations Command Center");
    setDescription("Real-time operational triage, lead conversion, and platform metrics • All times in IST (UTC+05:30)");
    setActions(
      <div className="flex items-center gap-2.5">
        <span className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold px-2.5 py-1 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          Live Sync
        </span>
        <Button
          size="sm"
          onClick={() => navigate(ROUTES.ADMIN_LIVE_VIEW.path)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs h-8 px-3.5 shadow-sm flex items-center gap-1.5"
        >
          <Radio className="w-3.5 h-3.5" />
          Open Live View
        </Button>
      </div>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  const pendingInquiriesCount = dashboardStats?.pendingInquiries?.total || 0;
  const totalPendingAttention = unassignedLeadsCount + pendingTutorsCount + pendingInquiriesCount;

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Urgent Action Ribbon (Concept 1) */}
      {totalPendingAttention > 0 && (
        <div className="bg-white rounded-xl border border-amber-200/80 bg-gradient-to-r from-amber-50/70 via-white to-white p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-slate-900">Immediate Attention Required</h4>
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full font-mono">
                  {totalPendingAttention} action items
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-0.5">
                <strong className="text-slate-800">{unassignedLeadsCount} Inbound demo requests</strong> awaiting tutor match •{" "}
                <strong className="text-slate-800">{pendingInquiriesCount} Support inquiries</strong> pending reply •{" "}
                <strong className="text-slate-800">{pendingTutorsCount} Tutor applications</strong> in approval queue.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {pendingTutorsCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(ROUTES.ADMIN_TUTORS_APPROVAL_QUEUE.path)}
                className="text-xs h-8 text-amber-800 border-amber-300 hover:bg-amber-100 font-semibold"
              >
                Approval Queue ({pendingTutorsCount})
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                const el = document.getElementById("admin-workstation");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold h-8 shadow-sm"
            >
              Triage Inbound Leads
            </Button>
          </div>
        </div>
      )}

      {/* 2. Precision KPI Overview Cards (Concept 1) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          stat={dashboardStats?.totalUsers}
          icon={<Users className="w-4 h-4" />}
          description="Registered students, parents & staff"
          isLoading={isStatsLoading}
          isError={isStatsError}
          linkTo={ROUTES.ADMIN_USERS_LIST.path}
        />
        <StatCard
          title="Active Tutors"
          stat={dashboardStats?.activeTutors}
          icon={<GraduationCap className="w-4 h-4" />}
          description="Verified and live for bookings"
          isLoading={isStatsLoading}
          isError={isStatsError}
          linkTo={ROUTES.ADMIN_TUTORS_LIST.path}
        />
        <StatCard
          title="Total Sessions"
          stat={dashboardStats?.totalSessions}
          icon={<Calendar className="w-4 h-4" />}
          description="Scheduled and completed"
          isLoading={isStatsLoading}
          isError={isStatsError}
          linkTo={ROUTES.ADMIN_SESSIONS_LIST.path}
        />
        <StatCard
          title="Pending Inquiries"
          stat={dashboardStats?.pendingInquiries}
          icon={<MessageSquare className="w-4 h-4" />}
          description="Inbound questions needing response"
          isLoading={isStatsLoading}
          isError={isStatsError}
          linkTo={ROUTES.ADMIN_INQUIRIES.path}
        />
      </div>

      {/* 3. The Admin Triage Workstation (Concept 3: Unified Tabbed Workstation) */}
      <div id="admin-workstation">
        <AdminTriageWorkstation />
      </div>

      {/* 4. Administrative Fast-Path Actions (Replaced console.log mock buttons with real operational deep-links) */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">Administrative Fast-Path Actions</h3>
            <p className="text-xs text-slate-500 mt-0.5">Direct shortcuts to high-frequency admin workflows</p>
          </div>
          <span className="text-xs font-mono text-slate-400">4 Core Tools</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            onClick={() => navigate(ROUTES.ADMIN_ENROLLMENTS_CREATE.path)}
            className="group p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-all cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <UserPlus className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">
                Manual Enrollment
              </h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Enroll a student into a customized batch with specific subjects and installment plans.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-indigo-600">
              <span>Enroll student</span>
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </div>

          <div
            onClick={() => navigate(ROUTES.ADMIN_TUTORS_APPROVAL_QUEUE.path)}
            className="group p-4 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-all cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <UserCheck className="w-5 h-5" />
              </div>
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                  Tutor Approval Queue
                </h4>
                {pendingTutorsCount > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    {pendingTutorsCount} new
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Review applicant resumes, verify academic credentials, and approve tutor profiles.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-emerald-600">
              <span>Review applicants</span>
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </div>

          <div
            onClick={() => navigate(ROUTES.ADMIN_SESSIONS_SCHEDULE.path)}
            className="group p-4 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 transition-all cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="w-9 h-9 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <CalendarPlus className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900 group-hover:text-purple-700 transition-colors">
                Schedule Session
              </h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Match a student with a tutor, allocate time slots in IST, and dispatch calendar invites.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-purple-600">
              <span>Book session</span>
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </div>

          <div
            onClick={() => navigate(ROUTES.ADMIN_CURRICULUM_CATALOG.path)}
            className="group p-4 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 transition-all cursor-pointer flex flex-col justify-between"
          >
            <div>
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                <Layers className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                Curriculum Catalog
              </h4>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Manage boards, subjects, domain-unit hierarchies, and canonical learning outcomes.
              </p>
            </div>
            <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-amber-700">
              <span>Open catalog</span>
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
